import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { MenRoomLockerRow } from './LockersMenRoomLayout';

/**
 * Administration lockers: 2 rows × 20 columns, labels 1–40 (odds top, evens bottom).
 */
const TILE = 40;
const NUM_COLS = 20;

export function normalizeAdministrationSlotKey(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  if (!/^0*\d+$/.test(s)) return null;
  const n = parseInt(s, 10);
  if (Number.isNaN(n) || n < 1 || n > 40) return null;
  return String(n);
}

function formatUser(u: NonNullable<MenRoomLockerRow['assignee']>) {
  return `${u.firstName} ${u.lastName}${u.department ? ` (${u.department})` : ''}`;
}

export interface LockersAdministrationLayoutProps {
  lockersByLabel: Map<string, MenRoomLockerRow>;
  canManage: boolean;
  onSelectSlot: (row: MenRoomLockerRow | null, canonicalLabel: string) => void;
}

const LockersAdministrationLayout: React.FC<LockersAdministrationLayoutProps> = ({
  lockersByLabel,
  canManage,
  onSelectSlot
}) => {
  const { t } = useTranslation();

  const renderTile = (canonicalLabel: string) => {
    const row = lockersByLabel.get(canonicalLabel) ?? null;
    const hasRecord = !!row;
    const occupied = !!(row && (row.userId || (row.externalOccupantLabel && row.externalOccupantLabel.trim())));

    let bg: string;
    let borderColor: string;
    if (!hasRecord) {
      bg = 'action.hover';
      borderColor = 'divider';
    } else if (occupied) {
      bg = 'warning.light';
      borderColor = 'warning.main';
    } else {
      bg = 'success.light';
      borderColor = 'success.main';
    }

    const titleParts = [`#${canonicalLabel}`];
    if (!hasRecord) {
      titleParts.push(
        t(
          'lockers.layoutNotConfigured',
          'Not in the system yet — click to create this locker and optionally assign someone'
        )
      );
    } else if (occupied) {
      if (row.assignee) titleParts.push(formatUser(row.assignee));
      else if (row.externalOccupantLabel?.trim()) titleParts.push(row.externalOccupantLabel.trim());
      else titleParts.push(t('lockers.free', 'Free'));
    } else {
      titleParts.push(t('lockers.free', 'Free'));
    }

    const clickable = hasRecord || canManage;
    const title = titleParts.join(' — ');

    return (
      <Tooltip title={title}>
        <Box
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={() => clickable && onSelectSlot(row, canonicalLabel)}
          onKeyDown={(e) => {
            if (!clickable) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectSlot(row, canonicalLabel);
            }
          }}
          sx={{
            width: TILE,
            height: TILE,
            boxSizing: 'border-box',
            bgcolor: bg,
            border: '2px solid',
            borderColor,
            borderRadius: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.72rem',
            fontWeight: 600,
            cursor: clickable ? 'pointer' : 'default',
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
          }}
        >
          {canonicalLabel}
        </Box>
      </Tooltip>
    );
  };

  const cols = `repeat(${NUM_COLS}, ${TILE}px)`;
  const rows = `repeat(2, ${TILE}px)`;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        {t('lockers.roomLayoutTitleAdministration', 'Administration layout')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: cols,
            gridTemplateRows: rows,
            gap: 0.5,
            p: 1.5,
            border: '3px solid',
            borderColor: 'text.primary',
            borderRadius: 1,
            width: 'fit-content'
          }}
        >
          {Array.from({ length: NUM_COLS }, (_, i) => {
            const col = i + 1;
            const topLabel = String(2 * col - 1);
            const bottomLabel = String(2 * col);
            return (
              <React.Fragment key={`col-${col}`}>
                <Box
                  sx={{ gridColumn: col, gridRow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                  {renderTile(topLabel)}
                </Box>
                <Box
                  sx={{ gridColumn: col, gridRow: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                  {renderTile(bottomLabel)}
                </Box>
              </React.Fragment>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                bgcolor: 'success.light',
                border: '2px solid',
                borderColor: 'success.main',
                borderRadius: 0.5
              }}
            />
            <Typography variant="caption">{t('lockers.legendFree', 'Free locker')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                bgcolor: 'warning.light',
                border: '2px solid',
                borderColor: 'warning.main',
                borderRadius: 0.5
              }}
            />
            <Typography variant="caption">{t('lockers.legendTaken', 'Taken locker')}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LockersAdministrationLayout;
